import { useState } from 'react'
import { ImageLightbox } from '@/components/ImageLightbox'
import { trackImageError } from '@/utils/imageTelemetry'

interface ImageGridImage {
    url: string
    fullsize?: string
    alt?: string
    aspectRatio?: { width: number; height: number }
}

interface ImageGridProps {
    images: ImageGridImage[]
    /** Stacked layout for detail pages (all images full-width) vs grid for feed cards */
    layout?: 'grid' | 'stacked'
}

export function ImageGrid({ images, layout = 'grid' }: ImageGridProps) {
    const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)

    const openLightbox = (index: number, e: React.MouseEvent) => {
        e.stopPropagation()
        setLightboxIndex(index)
    }

    return (
        <>
            {layout === 'stacked' ? (
                <div className="rounded-lg overflow-hidden border border-border-light dark:border-border-dark">
                    {images.slice(0, 4).map((img, i) => (
                        <div
                            key={i}
                            className="relative cursor-pointer bg-gray-100 dark:bg-gray-800"
                            onClick={e => openLightbox(i, e)}
                        >
                            <img
                                src={img.url}
                                alt={img.alt ?? ''}
                                className="w-full max-h-[300px] object-cover"
                                loading="lazy"
                                onError={e => {
                                    trackImageError('embed', img.url)
                                    const parent = e.currentTarget.parentElement
                                    if (parent) parent.style.display = 'none'
                                }}
                            />
                            {img.alt && <AltBadge />}
                        </div>
                    ))}
                </div>
            ) : images.length === 1 ? (
                <div
                    className={`relative rounded-lg overflow-hidden border border-border-light dark:border-border-dark cursor-pointer bg-gray-100 dark:bg-gray-800${images[0].aspectRatio ? '' : ' min-h-[200px]'}`}
                    onClick={e => openLightbox(0, e)}
                >
                    <img
                        src={images[0].url}
                        alt={images[0].alt ?? ''}
                        className="w-full max-h-[400px] object-cover"
                        loading="lazy"
                        style={
                            images[0].aspectRatio
                                ? {
                                      aspectRatio: `${images[0].aspectRatio.width}/${images[0].aspectRatio.height}`,
                                  }
                                : undefined
                        }
                        onError={e => {
                            trackImageError('embed', images[0].url)
                            const parent = e.currentTarget.parentElement
                            if (parent) parent.style.display = 'none'
                        }}
                    />
                    {images[0].alt && <AltBadge />}
                </div>
            ) : (
                <div className="grid grid-cols-2 gap-0.5 rounded-lg overflow-hidden border border-border-light dark:border-border-dark">
                    {images.slice(0, 4).map((img, i) => (
                        <div
                            key={i}
                            className="relative cursor-pointer bg-gray-100 dark:bg-gray-800"
                            onClick={e => openLightbox(i, e)}
                        >
                            <img
                                src={img.url}
                                alt={img.alt ?? ''}
                                className="w-full h-[150px] object-cover"
                                loading="lazy"
                                onError={e => {
                                    trackImageError('embed', img.url)
                                    const parent = e.currentTarget.parentElement
                                    if (parent) parent.style.display = 'none'
                                }}
                            />
                            {img.alt && <AltBadge />}
                        </div>
                    ))}
                </div>
            )}

            {lightboxIndex !== null && (
                <ImageLightbox
                    images={images}
                    initialIndex={lightboxIndex}
                    onClose={() => setLightboxIndex(null)}
                />
            )}
        </>
    )
}

function AltBadge() {
    return (
        <span className="absolute bottom-1.5 right-1.5 px-1.5 py-0.5 text-[10px] font-bold leading-none bg-black/70 text-white rounded">
            ALT
        </span>
    )
}
